import { Alert, message, Spin, Tag } from 'antd';
import { Edit3, Plus, RefreshCcw, Save, ShieldCheck } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { AppConfigPublishDialog } from '@/components/common/AppConfigPublishDialog';
import { AppConfigVersionPanel } from '@/components/common/AppConfigVersionPanel';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { authStore, llmAccessConfigStore } from '@/stores';
import type { AccessAppPolicy, AccessRule } from '@/types';
import { AccessAppDialog } from './components/AccessAppDialog';

interface AccessRow {
  id: string;
  policy: AccessAppPolicy;
  effective: AccessRule;
}

function inheritedRule(defaults: AccessRule, app: AccessAppPolicy): AccessRule {
  return {
    enabled: app.enabled ?? defaults.enabled,
    allowed_protocols: app.allowed_protocols ?? defaults.allowed_protocols,
    allowed_models: app.allowed_models ?? defaults.allowed_models,
    allowed_providers: app.allowed_providers ?? defaults.allowed_providers,
    allowed_routes: app.allowed_routes ?? defaults.allowed_routes,
  };
}

function listSummary(values?: string[]): string {
  if (!values || values.length === 0) return '空';
  return values.length <= 2
    ? values.join(', ')
    : `${values.slice(0, 2).join(', ')} 等 ${values.length} 项`;
}

export const LLMAccessPage = observer(() => {
  const canRead = authStore.can(ADMIN_PERMISSIONS.llmAccessRead);
  const canWrite = authStore.can(ADMIN_PERMISSIONS.llmAccessWrite);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const deploymentPending = llmAccessConfigStore.deploymentPending;

  useEffect(() => {
    if (canRead) void llmAccessConfigStore.load();
  }, [canRead]);
  useEffect(() => {
    if (!deploymentPending) return;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      try {
        await llmAccessConfigStore.load();
      } finally {
        if (!stopped && llmAccessConfigStore.deploymentPending) {
          timer = window.setTimeout(() => void poll(), 3000);
        }
      }
    };
    timer = window.setTimeout(() => void poll(), 3000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [deploymentPending]);

  const rows: AccessRow[] = (() => {
    const document = llmAccessConfigStore.document;
    if (!document) return [];
    return Object.entries(document.apps)
      .map(([id, policy]) => ({
        id,
        policy,
        effective: inheritedRule(document.defaults, policy),
      }))
      .sort((a, b) => a.id.localeCompare(b.id));
  })();

  const columns: Column<AccessRow>[] = [
    { id: 'app', header: 'App ID', accessor: (row) => <code>{row.id}</code> },
    {
      id: 'enabled',
      header: '有效状态',
      accessor: (row) => (
        <>
          <Tag color={row.effective.enabled ? 'green' : undefined}>
            {row.effective.enabled ? '启用' : '停用'}
          </Tag>
          {row.policy.enabled == null ? <Tag>继承</Tag> : null}
        </>
      ),
    },
    {
      id: 'models',
      header: '有效模型',
      accessor: (row) => listSummary(row.effective.allowed_models),
    },
    {
      id: 'providers',
      header: '有效 Provider',
      accessor: (row) => listSummary(row.effective.allowed_providers),
    },
    {
      id: 'routes',
      header: '有效 Route',
      accessor: (row) => listSummary(row.effective.allowed_routes),
    },
    {
      id: 'users',
      header: 'User Override',
      accessor: (row) => Object.keys(row.policy.users || {}).length,
    },
    {
      id: 'action',
      header: '操作',
      accessor: (row) =>
        canWrite ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditingID(row.id);
              setEditorOpen(true);
            }}
          >
            <Edit3 className="h-4 w-4" />
          </Button>
        ) : null,
    },
  ];

  const preparePublish = async () => {
    if (await llmAccessConfigStore.validate()) setPublishOpen(true);
  };
  const confirmPublish = async () => {
    if (await llmAccessConfigStore.publish()) {
      setPublishOpen(false);
      message.success('LLM 访问策略已发布');
    }
  };

  if (!canRead) {
    return (
      <Alert type="warning" showIcon message="没有读取 LLM 访问策略的权限" />
    );
  }

  const editingPolicy =
    editingID && llmAccessConfigStore.document
      ? llmAccessConfigStore.document.apps[editingID]
      : null;

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="space-y-5 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold">
              <ShieldCheck className="h-5 w-5" /> LLM 访问策略
            </h1>
            <div className="mt-1 text-xs text-muted-foreground">
              展示 Defaults → App 的有效值；User Override 保留但一期只读
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void llmAccessConfigStore.load()}
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> 刷新
            </Button>
            {canWrite ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingID(null);
                    setEditorOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> 新增 App
                </Button>
                <Button
                  disabled={
                    llmAccessConfigStore.validating ||
                    deploymentPending ||
                    llmAccessConfigStore.baseVersion <= 0
                  }
                  onClick={() => void preparePublish()}
                >
                  <Save className="mr-2 h-4 w-4" /> 校验并发布
                </Button>
              </>
            ) : null}
          </div>
        </div>
        {llmAccessConfigStore.error ? (
          <Alert type="error" showIcon message={llmAccessConfigStore.error} />
        ) : null}
        {llmAccessConfigStore.warning ? (
          <Alert
            type="warning"
            showIcon
            message={llmAccessConfigStore.warning}
          />
        ) : null}
        <Spin spinning={llmAccessConfigStore.loading}>
          <DataTable columns={columns} data={rows} rowKey="id" />
        </Spin>
        <div className="text-xs text-muted-foreground">
          Policy Version: {llmAccessConfigStore.document?.policy_version || '-'}{' '}
          · Base Version: {llmAccessConfigStore.baseVersion || '-'}
        </div>
        <AppConfigVersionPanel
          currentVersion={llmAccessConfigStore.currentVersion}
          hostedVersions={llmAccessConfigStore.recentHostedVersions}
          deployments={llmAccessConfigStore.recentDeployments}
          canRollback={canWrite && !deploymentPending}
          rollingBack={llmAccessConfigStore.publishing}
          onRollback={(version) => llmAccessConfigStore.rollback(version)}
        />
      </div>

      <AccessAppDialog
        open={editorOpen}
        appID={editingID}
        policy={editingPolicy}
        onClose={() => setEditorOpen(false)}
        onSave={(appID, policy, previousID) => {
          if (
            previousID !== appID &&
            llmAccessConfigStore.document?.apps[appID]
          ) {
            message.error(`App ID ${appID} 已存在`);
            return;
          }
          llmAccessConfigStore.updateApp(appID, policy, previousID);
          setEditorOpen(false);
        }}
      />
      <AppConfigPublishDialog
        open={publishOpen}
        title="发布 LLM 访问策略"
        before={llmAccessConfigStore.originalDocument}
        after={llmAccessConfigStore.document}
        validation={llmAccessConfigStore.validation}
        publishing={llmAccessConfigStore.publishing}
        deploymentPending={deploymentPending}
        onCancel={() => setPublishOpen(false)}
        onConfirm={() => void confirmPublish()}
        showAccessPreview
      />
    </div>
  );
});
